/**
 * Audit Log Page
 * Visar alla kommandon och queries som användaren har skickat till enheter
 * 
 * Refaktorerad att använda useAsyncData och usePagination
 */

import { useState, useEffect, useCallback } from 'react';
import { ROUTES } from '@/constants/routes';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/store/languageStore';
import { usePagination, useApiCall, useAsyncData } from '@/hooks';
import { auditLogService, type AuditLogEntry, type AuditLogFilters } from '@/services/auditLog/auditLogService';
import { ArrowLeftIcon, ChevronDownIcon, TrashIcon, ClipboardDocumentListIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '@/services/api/client';
import toast from 'react-hot-toast';

import DateInput from '@/components/common/DateInput';

const AuditLog = () => {
 const navigate = useNavigate();
 const { t } = useTranslation();
 const [filters, setFilters] = useState<AuditLogFilters>({
 imei: '',
 command_code: '',
 status: '',
 dateFrom: '',
 dateTo: '',
 });
 const [commandQueueStatus, setCommandQueueStatus] = useState<Record<string, any>>({});
 const [responseModal, setResponseModal] = useState<{ open: boolean; title: string; data: any } | null>(null);
 
  const pagination = usePagination({ initialPageSize: 100 });
  const { execute: deleteCommand } = useApiCall();

  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/audit-log/my-commands', {
        params: {
          ...filters,
          limit: pagination.pageSize,
          offset: pagination.offset,
        },
      });
      if (response.data?.success) {
        setLogs(response.data.data || []);
        if (response.data.pagination?.total !== undefined) {
          pagination.setTotal(response.data.pagination.total);
        }
      }
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.pageSize, pagination.offset]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const refetch = fetchLogs;

 // Hämta statistik
 const { data: statistics } = useAsyncData<any>(
 '/api/audit-log/my-statistics',
 {
 params: {
 dateFrom: filters.dateFrom || undefined,
 dateTo: filters.dateTo || undefined,
 },
 deps: [filters.dateFrom, filters.dateTo],
 }
 );

 // Ladda kommandon-status från kön för varje unik IMEI
 useEffect(() => {
 const loadQueueStatuses = async () => {
 if (logs.length === 0) return;
 
 const uniqueImeis = [...new Set(logs.map(log => log.imei))];
 const statuses: Record<string, any> = {};
 
 for (const imei of uniqueImeis) {
 try {
 const queueData = await auditLogService.getDeviceCommandQueue(imei);
 if (queueData.success && queueData.data) {
 statuses[imei] = queueData.data;
 }
 } catch {
 // Ignore errors for queue status
 }
 }
 
 setCommandQueueStatus(statuses);
 };

 loadQueueStatuses();
 }, [logs]);

 const handleFilterChange = useCallback((key: keyof AuditLogFilters, value: string) => {
 setFilters(prev => ({ ...prev, [key]: value }));
 pagination.firstPage();
 }, [pagination]);

 const handleDeleteCommand = useCallback(async (commandId: number, status: string) => {
 if (status === 'acknowledged') {
 toast.error(t('copy.admin.auditCannotDeleteAcknowledged'));
 return;
 }

 if (!confirm(t('copy.admin.auditDeleteCommandConfirm'))) {
 return;
 }

 await deleteCommand(
 () => auditLogService.deleteCommand(commandId),
 {
 successMessage: 'Kommandot har tagits bort',
 errorMessage: 'Kunde inte ta bort kommandot',
 onSuccess: () => refetch(),
 }
 );
 }, [deleteCommand, refetch]);

 const getStatusBadgeClass = (status: string) => {
 const classes: Record<string, string> = {
 acknowledged: 'bg-green-100 text-green-800',
 sent: 'bg-blue-100 text-blue-800',
 pending: 'bg-yellow-100 text-yellow-800',
 failed: 'bg-red-100 text-red-800',
 timeout: 'bg-red-100 text-red-800',
 };
 return classes[status] || 'bg-bgBase text-textDefault';
 };

 const formatDate = (dateString: string) => {
 return new Date(dateString).toLocaleString('sv-SE', {
 year: 'numeric',
 month: '2-digit',
 day: '2-digit',
 hour: '2-digit',
 minute: '2-digit',
 second: '2-digit',
 });
 };

 return (
 <div className="p-6 max-w-7xl mx-auto">
 <div className="mb-6">
 <div className="flex items-center gap-3">
 <button onClick={() => navigate(ROUTES.SYSADMIN)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
 <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
 </button>
 <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
 <ClipboardDocumentListIcon className="w-6 h-6 text-primary" />
 {t('auditLog.title')}
 </h1>
 </div>
 <p className="mt-2 text-sm text-textSecondary">
 {t('auditLog.description')}
 </p>
 </div>

 {/* Statistik */}
 {statistics && (
 <div className="mb-6 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
 <div className="bg-bgElevated p-4 rounded-lg shadow">
 <div className="text-sm text-textSecondary">{t('auditLog.total')}</div>
 <div className="text-2xl font-bold text-textDefault">{statistics.total_commands}</div>
 </div>
 <div className="bg-bgElevated p-4 rounded-lg shadow">
 <div className="text-sm text-textSecondary">{t('auditLog.successful')}</div>
 <div className="text-2xl font-bold text-green-600">{statistics.successful}</div>
 </div>
 <div className="bg-bgElevated p-4 rounded-lg shadow">
 <div className="text-sm text-textSecondary">{t('auditLog.pending')}</div>
 <div className="text-2xl font-bold text-yellow-600">{statistics.pending}</div>
 </div>
 <div className="bg-bgElevated p-4 rounded-lg shadow">
 <div className="text-sm text-textSecondary">{t('auditLog.failed')}</div>
 <div className="text-2xl font-bold text-red-600">{statistics.failed}</div>
 </div>
 <div className="bg-bgElevated p-4 rounded-lg shadow">
 <div className="text-sm text-textSecondary">{t('auditLog.devices')}</div>
 <div className="text-2xl font-bold text-textDefault">{statistics.unique_devices}</div>
 </div>
 <div className="bg-bgElevated p-4 rounded-lg shadow">
 <div className="text-sm text-textSecondary">{t('auditLog.commandTypes')}</div>
 <div className="text-2xl font-bold text-textDefault">{statistics.unique_command_types}</div>
 </div>
 </div>
 )}

 {/* Filter */}
 <div className="bg-bgElevated p-4 rounded-lg shadow mb-6">
 <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
 <div>
 <label className="block text-sm font-medium text-textDefault mb-1">IMEI</label>
 <input
 type="text"
 placeholder={t('auditLog.filterImei')}
 value={filters.imei}
 onChange={(e) => handleFilterChange('imei', e.target.value)}
 className="w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-bgElevated text-textDefault"
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-textDefault mb-1">{t('auditLog.command')}</label>
 <input
 type="text"
 placeholder={t('auditLog.filterCommand')}
 value={filters.command_code}
 onChange={(e) => handleFilterChange('command_code', e.target.value)}
 className="w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-bgElevated text-textDefault"
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-textDefault mb-1">{t('common.status')}</label>
 <select
 value={filters.status}
 onChange={(e) => handleFilterChange('status', e.target.value)}
 className="w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-bgElevated text-textDefault"
 >
 <option value="">{t('auditLog.filterStatus')}</option>
 <option value="pending">{t('auditLog.statusPending')}</option>
 <option value="sent">{t('auditLog.statusSent')}</option>
 <option value="acknowledged">{t('auditLog.statusAcknowledged')}</option>
 <option value="failed">{t('auditLog.statusFailed')}</option>
 <option value="timeout">{t('auditLog.statusTimeout')}</option>
 </select>
 </div>
 <div>
 <label className="block text-sm font-medium text-textDefault mb-1">{t('auditLog.filterDateFrom')}</label>
 <DateInput value={filters.dateFrom}
 onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
 className="w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-bgElevated text-textDefault"
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-textDefault mb-1">{t('auditLog.filterDateTo')}</label>
 <DateInput value={filters.dateTo}
 onChange={(e) => handleFilterChange('dateTo', e.target.value)}
 className="w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-bgElevated text-textDefault"
 />
 </div>
 </div>
 </div>

 {/* Tabell */}
 <div className="bg-bgElevated rounded-lg shadow overflow-hidden">
 {loading ? (
 <div className="p-8 text-center">
 <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
 <p className="mt-2 text-textSecondary">{t('common.loading')}</p>
 </div>
 ) : logs.length === 0 ? (
 <div className="p-8 text-center text-textSecondary">
 {t('auditLog.noCommands')}
 </div>
 ) : (
 <>
 <div className="overflow-x-auto">
 <table className="min-w-full divide-y divide-border">
 <thead className="bg-bgContainer">
 <tr>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">{t('auditLog.dateTime')}</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">{t('auditLog.device')}</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">{t('auditLog.lastSeen')}</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">{t('auditLog.command')}</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">{t('auditLog.parameters')}</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">{t('common.status')}</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">{t('auditLog.queueStatus')}</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">{t('auditLog.response')}</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">{t('common.actions')}</th>
 </tr>
 </thead>
 <tbody className="bg-bgElevated divide-y divide-border">
 {logs.map((log) => (
 <tr key={log.id} className="hover:bg-bgBase">
 <td className="px-6 py-4 whitespace-nowrap text-sm text-textDefault">
 {formatDate(log.created_at)}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-textDefault">
 <div>
 <div className="font-medium">{log.imei}</div>
 {log.vehicle_name && (
 <div className="text-xs text-textSecondary">{log.vehicle_name}</div>
 )}
 </div>
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-textDefault">
 {log.last_seen ? (
 <div>
 <div className="font-medium text-green-600 dark:text-green-400">{formatDate(log.last_seen)}</div>
 <div className="text-xs text-textSecondary">
 {(() => {
 const diffMs = Date.now() - new Date(log.last_seen).getTime();
 const diffMins = Math.floor(diffMs / 60000);
 const diffHours = Math.floor(diffMins / 60);
 const diffDays = Math.floor(diffHours / 24);
 if (diffMins < 1) return t('auditLog.justNow');
 if (diffMins < 60) return t('auditLog.minutesAgo').replace('{count}', String(diffMins));
 if (diffHours < 24) return t('auditLog.hoursAgo').replace('{count}', String(diffHours));
 return t('auditLog.daysAgo').replace('{count}', String(diffDays));
 })()}
 </div>
 </div>
 ) : (
 <div className="text-textTertiary italic">{t('auditLog.never')}</div>
 )}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-textDefault">
 <div className="space-y-1">
 <div className="font-semibold text-base">{log.command_name}</div>
 <div className="flex items-center gap-2">
 <code className="text-xs text-textSecondary bg-bgBase px-2 py-1 rounded">
 {log.command_code}
 </code>
 {log.command_type && (
 <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded ${
 log.command_type === 'GET' ? 'bg-blue-100 text-blue-800' :
 log.command_type === 'SET' ? 'bg-yellow-100 text-yellow-800' :
 log.command_type === 'ACTION' ? 'bg-green-100 text-green-800' :
 'bg-bgBase text-textDefault'
 }`}>
 {log.command_type}
 </span>
 )}
 </div>
 </div>
 </td>
 <td className="px-6 py-4 text-sm text-textDefault">
 {log.parameters && Object.keys(log.parameters).length > 0 ? (
 <details className="cursor-pointer">
 <summary className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1">
 <ChevronDownIcon className="w-4 h-4 inline" />
 {t('auditLog.showCount').replace('{count}', String(Object.keys(log.parameters).length))}
 </summary>
 <pre className="mt-2 text-xs bg-bgLight p-2 rounded overflow-auto max-w-md">
 {JSON.stringify(log.parameters, null, 2)}
 </pre>
 </details>
 ) : (
 <span className="text-textTertiary italic">Inga</span>
 )}
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(log.status)}`}>
 {log.status}
 </span>
 {log.error_message && (
 <div className="mt-1 text-xs text-red-600 dark:text-red-400">{log.error_message}</div>
 )}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-textDefault">
 {commandQueueStatus[log.imei] ? (
 <span className="text-xs text-textSecondary">{t('auditLog.inQueue')}</span>
 ) : (
 <span className="text-textTertiary">-</span>
 )}
 </td>
          <td className="px-6 py-4 text-sm text-textDefault">
                {log.response_data ? (
                  <button
                    onClick={() => setResponseModal({
                      open: true,
                      title: `${log.command_name || log.command_code} — ${log.imei}`,
                      data: log.response_data,
                    })}
                    className="text-blue-600 dark:text-blue-400 hover:underline text-sm"
                  >
                    {t('auditLog.showResponse')}
                  </button>
                ) : (
                  <span className="text-textTertiary">-</span>
                )}
              </td>
 <td className="px-6 py-4 whitespace-nowrap">
 {log.status !== 'acknowledged' && (
 <button
 onClick={() => handleDeleteCommand(log.id, log.status)}
 className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
 title={t('auditLog.deleteCommand')}
 >
 <TrashIcon className="w-5 h-5" />
 </button>
 )}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>

 {/* Paginering */}
 {pagination.total > pagination.pageSize && (
 <div className="bg-bgContainer px-6 py-4 flex items-center justify-between">
 <div className="text-sm text-textDefault">
 {t('auditLog.showing')} {pagination.startIndex + 1} - {pagination.endIndex} {t('auditLog.of')} {pagination.total}
 </div>
 <div className="flex gap-2">
 <button
 onClick={pagination.previousPage}
 disabled={!pagination.hasPrevious}
 className="px-4 py-2 text-sm font-medium text-textDefault bg-bgBase border border-border rounded-md hover:bg-bgBase disabled:opacity-50 disabled:cursor-not-allowed"
 >
 {t('common.previous')}
 </button>
 <button
 onClick={pagination.nextPage}
 disabled={!pagination.hasNext}
 className="px-4 py-2 text-sm font-medium text-textDefault bg-bgBase border border-border rounded-md hover:bg-bgBase disabled:opacity-50 disabled:cursor-not-allowed"
 >
 {t('common.next')}
 </button>
 </div>
 </div>
 )}
 </>
 )}
  </div>

      {/* Svar-popup */}
      {responseModal?.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setResponseModal(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-xl mx-4 overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate pr-4">
                {responseModal.title}
              </h3>
              <button
                onClick={() => setResponseModal(null)}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0"
              >
                <XMarkIcon className="w-5 h-5 text-textTertiary dark:text-textTertiary" />
              </button>
            </div>
            <div className="p-5 max-h-96 overflow-auto">
              <pre className="text-xs text-gray-800 dark:text-gray-200 font-mono whitespace-pre-wrap break-all bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                {typeof responseModal.data === 'string'
                  ? responseModal.data
                  : JSON.stringify(responseModal.data, null, 2)}
              </pre>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setResponseModal(null)}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLog;
